import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusChip from "../components/StatusChip";
import BranchPicker from "../components/BranchPicker";
import { Search, Plus, Route as RouteIcon } from "lucide-react";

const dt = (s) => (s ? new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "");
const tm = dt;
const TYPE_SHORT = { home: "Home TD", showroom: "Showroom", connected: "Connected", utility: "Utility" };
const dayLabel = (s) => {
  const d = new Date(s); const today = new Date(); const tomorrow = new Date(Date.now() + 86400000);
  const same = (a, b) => a.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) === b.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
  return same(d, today) ? "Today" : same(d, tomorrow) ? "Tomorrow" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
};

export default function Availability() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get("/vehicles", { params: { branch_id: branchId || undefined } })
      .then((r) => setVehicles(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [branchId]);

  const filtered = useMemo(() => vehicles.filter((v) =>
    !q || v.reg_no.toLowerCase().includes(q.toLowerCase()) || v.model.toLowerCase().includes(q.toLowerCase())), [vehicles, q]);

  const counts = useMemo(() => {
    const c = { total: vehicles.length, available: 0, blocked: 0, on_test_drive: 0, workshop: 0, non_compliant: 0 };
    vehicles.forEach((v) => { c[v.status] = (c[v.status] || 0) + 1; if (v.non_compliant) c.non_compliant++; });
    return c;
  }, [vehicles]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((v) => (g[v.model] = g[v.model] || []).push(v));
    return Object.entries(g).sort();
  }, [filtered]);

  const canOperate = ["sales", "sm", "tdc", "admin"].includes(user.role);

  return (
    <div className="px-4 py-5 lg:px-8 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Live Availability</h1>
          <p className="text-xs text-slate-500 mt-0.5">Updates every 10s · IST</p>
        </div>
        <BranchPicker value={branchId} onChange={setBranchId} />
      </div>

      <div data-testid="summary-strip" className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[["Fleet", counts.total, "text-[#0E2240]"], ["Available", counts.available, "text-emerald-600"],
          ["Blocked", counts.blocked, "text-amber-600"], ["On TD", counts.on_test_drive, "text-blue-600"],
          ["Workshop", counts.workshop, "text-[#BB162B]"], ["Non-Compliant", counts.non_compliant, "text-slate-500"]].map(([l, n, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <div className={`font-heading text-xl font-bold ${c}`}>{n || 0}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</div>
          </div>
        ))}
      </div>

      {canOperate && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          <button data-testid="quick-block-car-btn" onClick={() => nav("/block")}
            className="flex items-center gap-2 whitespace-nowrap rounded-full bg-[#BB162B] px-4 py-2.5 text-sm font-bold text-white active:scale-95 transition-transform duration-150"><Plus size={16} /> Block a Car</button>
          <button data-testid="quick-my-trips-btn" onClick={() => nav("/trips")}
            className="flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-[#0E2240] active:scale-95 transition-transform duration-150"><RouteIcon size={16} /> Active Test Drives</button>
        </div>
      )}

      <div className="relative mt-4">
        <Search size={16} className="absolute left-3.5 top-3 text-slate-400" />
        <input data-testid="vehicle-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reg no or model…"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]" />
      </div>

      {loading ? (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-200" />)}</div>
      ) : grouped.map(([model, list]) => (
        <div key={model} className="mt-6">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{model} <span className="text-slate-300">· {list.length}</span></h2>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((v) => (
              <button key={v.id} data-testid={`vehicle-card-${v.reg_no}`} onClick={() => nav(`/vehicle/${v.id}`)}
                className="card-hover rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading text-base font-bold text-[#0E2240]">{v.reg_no}</div>
                    <div className="text-xs text-slate-500">{v.variant} · {v.fuel_type} · {v.color}</div>
                  </div>
                  <StatusChip status={v.status} />
                </div>
                {v.active_trip && (
                  <div data-testid={`ontd-badge-${v.reg_no}`} className="mt-2 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-[11px] text-indigo-800">
                    {v.active_trip.gated_out ? <><b>On TD since {tm(v.active_trip.started_ts)}</b> — due {tm(v.active_trip.due)}</> : <><b>Trip started {tm(v.active_trip.started_ts)}</b> — awaiting gate-out</>} · {v.active_trip.kec_name}{v.active_trip.type === "connected" && ` · Connected (${v.active_trip.customers})`}
                  </div>
                )}
                {v.active_block && !v.active_trip && (
                  <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                    <b>Blocked {tm(v.active_block.slot_start)}–{tm(v.active_block.block_until)}</b> — {v.active_block.kec_name}{v.active_block.type === "home" && " · Home TD"}
                  </div>
                )}
                {v.returning_to && (
                  <div data-testid={`returning-badge-${v.reg_no}`} className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800"><b>Returning to {v.returning_to}</b> — awaiting gate-out</div>
                )}
                {(v.upcoming || []).length > 0 && (
                  <div data-testid={`upcoming-${v.reg_no}`} className="mt-2 space-y-0.5 rounded-lg bg-sky-50 px-2.5 py-1.5 text-[11px] text-sky-800">
                    {v.upcoming.map((u, i) => (
                      <div key={i}><b>{dayLabel(u.slot_start)} {tm(u.slot_start)}</b> {TYPE_SHORT[u.type] || u.type} — {(u.kec_name || "").split(" ")[0]}{u.status === "pending_sm_approval" && " (awaiting SM)"}</div>
                    ))}
                    {v.upcoming_more > 0 && <div className="font-semibold">+{v.upcoming_more} more</div>}
                  </div>
                )}
                {v.on_loan_from && <div className="mt-2 inline-flex rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-bold text-indigo-700">ON LOAN from {v.on_loan_from}</div>}
                {v.non_compliant && <div className="mt-2 text-[11px] font-bold text-[#BB162B]">⚠ Non-compliant (insurance/PUC)</div>}
                <div className="mt-2 text-[11px] text-slate-400">{(v.current_odometer || 0).toLocaleString("en-IN")} km</div>
              </button>
            ))}
          </div>
        </div>
      ))}
      {!loading && vehicles.length === 0 && (
        <div data-testid="board-empty-state" className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="font-heading text-base font-bold text-[#0E2240]">No test drive cars added yet</div>
          <p className="mt-1 text-xs text-slate-400">Admin can add vehicles in Vehicle Master.</p>
        </div>
      )}
      {!loading && vehicles.length > 0 && filtered.length === 0 && <div className="mt-10 text-center text-sm text-slate-400">No vehicles found</div>}
    </div>
  );
}
